import { Seo } from "../components/Seo";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Terminal, KeyRound, Braces, Layers, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

const BASE = process.env.REACT_APP_BACKEND_URL;

function CodeBlock({ label, code, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Copied.");
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</span>
        <Button variant="ghost" size="sm" onClick={copy} className="h-7 text-slate-500">
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
        </Button>
      </div>
      <pre data-testid={testid} className="p-4 overflow-x-auto text-xs font-pnr text-slate-800 bg-white leading-relaxed whitespace-pre">{code}</pre>
    </div>
  );
}

const curlExample = `curl -X POST ${BASE}/api/convert \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"pnr_text": "*ABC123\\n 1.1SMITH/JOHN MR\\n 2 EK 202Y 09OCT JFKDXB HK1 1010P 705P\\n 4 HHL EK HK1 DXB 10OCT-17OCT\\n   ATLANTIS THE PALM"}'`;

const CAPABILITIES = [
  ["Flights", "Sabre & Amadeus air segments, airline names + logos, cabin, times, next-day arrivals with resolved arrival dates, flight duration, status"],
  ["Passengers", "Names, type (ADT/CHD/INF), per-traveller seats & e-tickets, frequent flyer"],
  ["E-tickets", "Ticket numbers matched to each passenger and airline (interline-aware)"],
  ["Hotels", "Property name, city, check-in/out, room type, guests, rate, confirmation"],
  ["Cars", "Supplier, vehicle category, pickup/return date, time & location"],
  ["Cruises", "Ship, cabin/stateroom, category, sailing dates"],
  ["Tours & Transfers", "Excursions/sightseeing and airport↔hotel transfers with dates"],
  ["Insurance", "Provider and policy number"],
  ["SSRs & Meals", "Special requests, meal codes (e.g. VGML), APIS/DOCS, wheelchair"],
  ["Fares & Payment", "Base/tax/total, form of payment (PAN masked), endorsements"],
  ["Remarks & Contacts", "Free-text remarks, phone/email contacts, record locator, ticketing time limit"],
];

const WHATS_NEW = [
  ["Geo & timezone data", "Each flight segment now includes origin/destination country, latitude/longitude, IANA timezone + short abbreviation, combined departure_datetime / arrival_datetime with weekday, Haversine distance_km / distance_miles, transit_time to the next leg, operated_by and aircraft"],
  ["Arrival dates", "Each flight now returns arrival_date (segments) and arrivalDate / arrivalRawDate (model) — explicit next-day dates (e.g. 705P/10OCT) and “+1 day” markers are resolved automatically"],
  ["Flight duration", "Every flight returns duration (e.g. “11h 55m”) plus durationMinutes on the model — timezone-accurate (DST-aware) using a bundled offline airport dataset, and also shown on rendered itineraries"],
  ["Layover times", "Connection gaps between consecutive legs are calculated and labelled on rendered itineraries (e.g. “2h 30m layover in London”)"],
  ["12-hour times", "Times display as friendly 6:00 PM on rendered itineraries, while the JSON keeps 24-hour HH:MM"],
  ["Year-normalised dates", "Rendered dates always include a 4-digit year (e.g. 12 Dec 2026), handling trips that cross New Year"],
  ["Local-time note", "Departure/arrival are clearly marked as local airport time to avoid long-haul confusion"],
  ["Manual builder", "Build an itinerary from scratch (no PNR) with searchable offline airline & airport autocomplete and calendar date / time pickers"],
];

const responseExample = `{
  "gds": "Sabre",
  "gds_confidence": 0.83,
  "segments": [
    {
      "gds": "Sabre",
      "airline_code": "EK",
      "airline_name": "Emirates",
      "airline_logo_url": "https://img.logo.dev/emirates.com?token=...",
      "flight_number": "202",
      "booking_class": "Y",
      "cabin_class": "Economy",
      "date_raw": "09OCT",
      "date": "9 Oct",
      "arrival_date": "10 Oct",
      "origin_code": "JFK",
      "origin_name": "John F. Kennedy International Airport",
      "origin_city": "New York",
      "destination_code": "DXB",
      "destination_name": "Dubai International Airport",
      "destination_city": "Dubai",
      "departure_time": "22:10",
      "arrival_time": "19:05",
      "duration": "11h 55m",
      "status_code": "HK1",
      "status": "Confirmed",
      "record_locator": "ABC123",
      "seat": "22A",
      "eticket": "1761234567890",
      "checkin_baggage": "2PC",
      "carry_on_baggage": "7KG",
      "frequent_flyer": "EK123456789",
      "meal": "Vegetarian (Indian)",
      "origin_country": "United States",
      "destination_country": "United Arab Emirates",
      "origin_latitude": 40.63975, "origin_longitude": -73.77893,
      "destination_latitude": 25.25278, "destination_longitude": 55.36444,
      "origin_timezone": "America/New_York", "destination_timezone": "Asia/Dubai",
      "origin_timezone_short": "EDT", "destination_timezone_short": "+04",
      "departure_datetime": "2026-10-09 22:10", "departure_day": "Fri",
      "arrival_datetime": "2026-10-10 19:05", "arrival_day": "Sat",
      "distance_km": 11000, "distance_miles": 6835,
      "transit_time": { "minutes": 150, "hours": 2.5 },
      "operated_by": null,
      "aircraft": null
    }
  ],
  "model": {
    "source":  { "gds": "Sabre", "confidence": 0.83, "level": "MEDIUM" },
    "pnr":     { "recordLocator": "ABC123", "creationDate": "2026-10-01", "officeId": "NYCXY1234", "receivedFrom": "JOHN SMITH" },
    "passengers": [
      {
        "id": "1.1", "lastName": "Smith", "firstName": "John", "title": "MR",
        "passengerType": "ADT", "passengerTypeName": "Adult",
        "seat": "22A", "eticket": "1761234567890", "frequentFlyer": "EK123456789",
        "tickets": [ { "airline": "EK", "number": "1761234567890" } ],
        "raw": "1.1SMITH/JOHN MR", "confidence": 0.85
      }
    ],
    "flights": [
      {
        "type": "AIR", "segmentNumber": 2,
        "marketingCarrier": "EK", "operatingCarrier": null, "codeshare": false,
        "airlineName": "Emirates", "flightNumber": "202",
        "bookingClass": "Y", "cabin": "Economy",
        "departureAirport": "JFK", "departureCity": "New York", "departureCountry": "United States",
        "arrivalAirport": "DXB", "arrivalCity": "Dubai", "arrivalCountry": "United Arab Emirates",
        "date": "2026-10-09", "rawDate": "09OCT",
        "arrivalDate": "2026-10-10", "arrivalRawDate": "10OCT", "arrivalNextDay": true,
        "departureTime": "22:10", "arrivalTime": "19:05",
        "duration": "11h 55m", "durationMinutes": 715,
        "statusCode": "HK", "statusDescription": "Confirmed", "passengerCount": 1,
        "terminal": null, "equipment": null, "meal": null, "seat": null,
        "gds": "Sabre", "confidence": 0.97
      }
    ],
    "hotels": [
      {
        "type": "HOTEL", "hotelName": "Atlantis The Palm", "city": "DXB", "address": null,
        "checkIn": "2026-10-10", "checkOut": "2026-10-17",
        "roomType": "Deluxe Ocean Room", "guests": "2 Adults",
        "rate": "450.00", "currency": "USD", "confirmationNumber": "H889900",
        "cancellation": null, "guarantee": null,
        "raw": "4 HHL EK HK1 DXB 10OCT-17OCT ATLANTIS THE PALM", "confidence": 0.8
      }
    ],
    "cars": [
      {
        "type": "CAR", "supplier": "Hertz", "supplierCode": "ZE", "city": "DXB",
        "pickupLocation": "DXB", "dropoffLocation": "DXB",
        "pickupDate": "2026-10-10", "pickupTime": "21:00",
        "dropoffDate": "2026-10-17", "dropoffTime": "06:00",
        "vehicleCategory": "Suv", "transmission": "Automatic",
        "rate": null, "currency": null, "confirmationNumber": "AVS456123",
        "raw": "5 CCR ZE HK1 DXB 10OCT-17OCT/HERTZ", "confidence": 0.7
      }
    ],
    "cruises": [
      {
        "type": "CRUISE", "shipName": "MSC Euribia", "category": "Balcony", "cabin": "12034",
        "sailingStart": "2026-10-13", "sailingEnd": "2026-10-17",
        "confirmationNumber": "CRUISE12345", "city": "DXB",
        "raw": "6 OTH CRUISE MSC EURIBIA ...", "confidence": 0.7
      }
    ],
    "tours": [
      {
        "type": "TOUR", "name": "Dubai Desert Safari", "city": "DXB", "date": "2026-10-12",
        "pickup": null, "timeFrom": "15:00", "timeTo": "22:00", "duration": null,
        "confirmationNumber": "T1", "raw": "TOUR DUBAI DESERT SAFARI 12OCT 1500-2200", "confidence": 0.75
      }
    ],
    "transfers": [
      { "type": "TRANSFER", "detail": "Airport-Hotel", "city": "DXB", "date": "2026-10-13",
        "confirmationNumber": null, "raw": "TRF DXB AIRPORT-HOTEL 13OCT", "confidence": 0.7 }
    ],
    "rail":      [],
    "insurance": [
      { "type": "INSURANCE", "provider": "Travel Insurance", "policyNumber": "ABC123456",
        "city": null, "raw": "INSURANCE TRAVEL INSURANCE POLICY ABC123456", "confidence": 0.75 }
    ],
    "seats": [
      { "type": "SEAT", "seats": [ { "seat": "22A", "passengerId": "1.1", "segment": "2" } ],
        "raw": "ST/22A/P1/S2", "confidence": 0.85 }
    ],
    "ssr": [
      { "code": "VGML", "description": "Vegetarian (Indian)", "airline": "EK", "status": "HK",
        "passengerId": "1.1", "text": "HK1/P1", "raw": "SSR VGML EK HK1/P1", "recognized": true,
        "confidence": 0.9, "flightIndex": 0, "associationConfidence": 0.9 }
    ],
    "osi":       [],
    "tickets": [
      { "kind": "ticket", "ticketNumber": "1761234567890", "airlinePrefix": "176", "status": "ISSUED",
        "passengerId": "1.1", "passengerName": null, "raw": "FA PAX 176-1234567890/ETEK/P1",
        "confidence": 0.85, "associationConfidence": 0.9 }
    ],
    "emds":      [],
    "payments":  [],
    "fares":     [],
    "remarks":   [],
    "contacts":  [
      { "type": "CONTACT", "kind": "email", "phone": null, "email": "john.smith@example.com",
        "belongsTo": null, "raw": "CTCE JOHN.SMITH//EXAMPLE.COM", "confidence": 0.8 }
    ],
    "addresses": [],
    "ticketing": {
      "ticketTimeLimit": null, "validatingCarrier": "EK", "receivedFrom": "JOHN SMITH",
      "endorsement": null, "commission": null, "corporateDiscount": null, "nonRefundable": null
    },
    "confidence":      { "overall": 0.82, "level": "MEDIUM", "uncertainCount": 0 },
    "validation":      { "ok": true, "notes": [] },
    "unknownSegments": [],
    "errors":          [],
    "classifiedLines": [ { "lineNumber": 1, "raw": "*ABC123", "text": "*ABC123", "type": "PNR_HEADER", "confidence": 0.9 } ]
  },
  "extras": {
    "frequent_flyer": [], "meals": [], "special_assistance": [], "ssr": [], "contacts": [],
    "fare": {}, "baggage": [], "eticket_numbers": [], "record_locator": null, "conditions": []
  },
  "warnings": [],
  "error": null,
  "calls_remaining": 19
}`;

const OPT = "Optional — only present when the PNR carries the data";
const FIELDS = [
  ["Flight — segments[] (flat, enriched)", [
    ["gds", "string", "Sabre · Amadeus · Generic"],
    ["airline_code", "string", "2-letter IATA marketing carrier"],
    ["airline_name", "string", "Resolved airline name (falls back to the code)"],
    ["airline_logo_url", "string | null", "Logo URL when available"],
    ["flight_number", "string", ""],
    ["booking_class", "string", "RBD letter, e.g. Y"],
    ["cabin_class", "string", "Economy · Premium Economy · Business · First"],
    ["date_raw", "string", "Departure date as written, e.g. 09OCT"],
    ["date", "string", "Departure date display, e.g. 9 Oct"],
    ["arrival_date", "string", "Resolved arrival date display (explicit /DDMMM, +1 markers and overnight legs handled)"],
    ["origin_code / destination_code", "string", "IATA airport codes"],
    ["origin_name / destination_name", "string", "Airport name (falls back to the code)"],
    ["origin_city / destination_city", "string", "City (empty string when unknown)"],
    ["departure_time / arrival_time", "string", "Local airport time, 24-hour HH:MM"],
    ["duration", "string", "Timezone-aware block time, e.g. 11h 55m (empty when it can't be computed)"],
    ["status_code", "string", "Raw GDS status incl. pax count, e.g. HK1"],
    ["status", "string", "Confirmed · Waitlisted · Pending · Cancelled …"],
    ["record_locator", "string", OPT + " (airline locator on the line)"],
    ["next_day", "boolean", OPT + " (only on native multi-line PNRs)"],
    ["seat, eticket, checkin_baggage, carry_on_baggage, frequent_flyer, meal", "string", OPT],
    ["origin_country / destination_country", "string | null", "Country name from the airports dataset"],
    ["origin_latitude, origin_longitude, destination_latitude, destination_longitude", "number | null", "Decimal degrees"],
    ["origin_timezone / destination_timezone", "string | null", "IANA zone, e.g. Asia/Kolkata"],
    ["origin_timezone_short / destination_timezone_short", "string | null", "Abbreviation at the local date/time, e.g. IST, GMT, +03"],
    ["departure_datetime / arrival_datetime", "string | null", "Local, YYYY-MM-DD HH:MM"],
    ["departure_day / arrival_day", "string | null", "3-letter weekday, e.g. Sat"],
    ["distance_km / distance_miles", "number | null", "Great-circle (Haversine) distance, rounded"],
    ["transit_time", "object", "{ minutes, hours } gap to the next segment's departure (timezone-aware); {} on the last segment"],
    ["operated_by / aircraft", "string | null", "Operating carrier / equipment when present in the PNR, else null"],
  ]],
  ["Flight — model.flights[]", [
    ["type", "\"AIR\"", ""],
    ["segmentNumber", "number | null", "GDS element number"],
    ["marketingCarrier / operatingCarrier", "string | null", "operatingCarrier is reserved; currently null"],
    ["codeshare", "boolean", "Reserved; currently false"],
    ["airlineName, flightNumber, bookingClass, cabin", "string", ""],
    ["departureAirport / arrivalAirport", "string", "IATA codes"],
    ["departureCity, departureCountry, arrivalCity, arrivalCountry", "string | null", ""],
    ["date / arrivalDate", "string", "ISO YYYY-MM-DD"],
    ["rawDate / arrivalRawDate", "string | null", "As written in the PNR, e.g. 10OCT"],
    ["departureTime / arrivalTime", "string", "24-hour HH:MM local"],
    ["arrivalNextDay", "boolean", "True when a +1/#1 marker (or overnight roll-over) applies"],
    ["duration / durationMinutes", "string / number | null", "Timezone-aware"],
    ["statusCode / statusDescription", "string", "e.g. HK / Confirmed"],
    ["passengerCount", "number | null", ""],
    ["terminal, equipment, meal, seat", "string | null", "Reserved slots; populated when present on the line"],
    ["gds, confidence", "string, number", ""],
  ]],
  ["Hotel — model.hotels[]", [
    ["type", "\"HOTEL\"", ""],
    ["hotelName, city, address", "string | null", ""],
    ["checkIn / checkOut", "string | null", "ISO YYYY-MM-DD"],
    ["roomType, guests", "string | null", ""],
    ["rate, currency", "string | null", ""],
    ["confirmationNumber, cancellation, guarantee", "string | null", ""],
    ["raw, confidence", "string, number", "Source text and 0–1 confidence"],
  ]],
  ["Car — model.cars[]", [
    ["type", "\"CAR\"", ""],
    ["supplier, supplierCode", "string | null", "e.g. Hertz / ZE"],
    ["city, pickupLocation, dropoffLocation", "string | null", ""],
    ["pickupDate / dropoffDate", "string | null", "ISO YYYY-MM-DD"],
    ["pickupTime / dropoffTime", "string | null", "24-hour HH:MM"],
    ["vehicleCategory, transmission", "string | null", ""],
    ["rate, currency, confirmationNumber", "string | null", ""],
    ["raw, confidence", "string, number", ""],
  ]],
  ["Cruise — model.cruises[]", [
    ["type", "\"CRUISE\"", ""],
    ["shipName, category, cabin", "string | null", ""],
    ["sailingStart / sailingEnd", "string | null", "ISO YYYY-MM-DD"],
    ["confirmationNumber, city", "string | null", ""],
    ["raw, confidence", "string, number", ""],
  ]],
  ["Excursion / Tour — model.tours[]", [
    ["type", "\"TOUR\"", ""],
    ["name, city", "string | null", ""],
    ["date", "string | null", "ISO YYYY-MM-DD"],
    ["pickup, timeFrom, timeTo, duration", "string | null", ""],
    ["confirmationNumber", "string | null", ""],
    ["raw, confidence", "string, number", ""],
  ]],
  ["Transfer / Rail — model.transfers[] · model.rail[]", [
    ["type", "\"TRANSFER\"", ""],
    ["detail, city", "string | null", ""],
    ["date", "string | null", "ISO YYYY-MM-DD"],
    ["confirmationNumber", "string | null", ""],
    ["raw, confidence", "string, number", ""],
  ]],
  ["Other model collections", [
    ["passengers[]", "object", "id, lastName, firstName, title, passengerType, passengerTypeName, raw, confidence + optional seat, eticket, frequentFlyer, tickets[{airline, number}]"],
    ["insurance[]", "object", "type, provider, policyNumber, city, raw, confidence"],
    ["seats[]", "object", "type, seats[{seat, passengerId, segment}], raw, confidence"],
    ["ssr[]", "object", "code, description, airline, status, passengerId, text, raw, recognized, confidence, flightIndex, associationConfidence + document{} (DOCS) / frequentFlyer (FQTV)"],
    ["tickets[]", "object", "kind, ticketNumber, airlinePrefix, status, passengerId, passengerName, raw, confidence, associationConfidence"],
    ["emds[] · payments[] · fares[]", "object", "emdNumber · method, brand, maskedNumber, last4, expiry · baseFare, taxes, total, currency, fareBasis"],
    ["contacts[] · addresses[] · osi[] · remarks[]", "object", "type/kind, phone, email, belongsTo · kind, text · code, carrier, text · type, qualifier, text"],
    ["pnr · ticketing", "object", "recordLocator, creationDate, officeId, receivedFrom · ticketTimeLimit, validatingCarrier, receivedFrom, endorsement, commission, corporateDiscount, nonRefundable"],
    ["confidence · validation · errors[] · classifiedLines[] · unknownSegments[]", "object", "Parser diagnostics: overall score, sanity notes, unmatched lines with reasons, per-line classification"],
  ]],
  ["Top level", [
    ["gds / gds_confidence", "string | null / number", "Detected GDS and 0–1 confidence"],
    ["segments[]", "array", "Flat enriched flights (above)"],
    ["model", "object", "Universal PNR Model (above)"],
    ["extras", "object", "frequent_flyer[], meals[], special_assistance[], ssr[], contacts[], fare{}, baggage[], eticket_numbers[], record_locator, conditions[] — legacy aggregate; may be {} for native multi-line PNRs"],
    ["warnings[]", "string[]", "Unknown airline/airport codes"],
    ["error", "string | null", ""],
    ["calls_remaining", "number | null", "null on unlimited tiers"],
  ]],
];

function FieldTable({ title, rows }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-700 uppercase tracking-wider">{title}</div>
      <div className="divide-y divide-slate-100">
        {rows.map(([name, type, desc]) => (
          <div key={name} className="grid grid-cols-1 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,3fr)] gap-1 sm:gap-3 px-4 py-2 text-xs">
            <code className="font-pnr text-slate-900 break-words">{name}</code>
            <span className="font-pnr text-slate-500">{type}</span>
            <span className="text-slate-600">{desc}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ApiDocs() {
  return (
    <div className="min-h-screen bg-slate-50">
      <Seo path="/developers" title="Developer API — decode full GDS PNRs programmatically" description="Integrate PNR Decode into your booking flow. One REST call turns a raw Sabre/Amadeus PNR into structured flights, hotels, cars, cruises, tours, transfers, insurance, passengers, seats, e-tickets, SSRs, fares and remarks." />
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 py-10">
        <Badge variant="outline" className="rounded-full border-blue-200 bg-blue-50 text-blue-700">REST API</Badge>
        <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight mt-3">API Documentation</h1>
        <p className="text-slate-500 mt-2">
          Send a raw Sabre or Amadeus PNR and get back a fully structured itinerary — not just flights, but hotels, cars,
          cruises, tours, transfers, insurance, passengers, seats, e-tickets, SSRs/meals, fares and remarks. It's the same
          engine that powers the web app. Grab your API key from your{" "}
          <Link to="/dashboard" className="text-blue-600 hover:underline">dashboard</Link>.
        </p>

        <section className="mt-8">
          <h2 className="font-heading font-bold text-slate-900 inline-flex items-center gap-2 mb-3"><Layers className="h-4 w-4 text-slate-400" /> What it decodes</h2>
          <div data-testid="docs-capabilities" className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
            {CAPABILITIES.map(([name, desc]) => (
              <div key={name} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 px-4 py-3">
                <span className="text-sm font-semibold text-slate-900 sm:w-40 shrink-0">{name}</span>
                <span className="text-sm text-slate-600">{desc}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-8">
          <h2 className="font-heading font-bold text-slate-900 inline-flex items-center gap-2 mb-3"><Sparkles className="h-4 w-4 text-blue-500" /> Recent additions</h2>
          <div data-testid="docs-whats-new" className="rounded-lg border border-blue-200 bg-blue-50/60 divide-y divide-blue-100">
            {WHATS_NEW.map(([name, desc]) => (
              <div key={name} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 px-4 py-3">
                <span className="text-sm font-semibold text-slate-900 sm:w-44 shrink-0">{name}</span>
                <span className="text-sm text-slate-600">{desc}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-2">The <code className="font-pnr">/api/convert</code> JSON returns <code className="font-pnr">arrival_date</code> and <code className="font-pnr">duration</code> per flight, and keeps raw 24-hour <code className="font-pnr">HH:MM</code> times and ISO dates. Purely visual touches (12-hour times, layovers, “+1 day”, local-time note) are applied when you render an itinerary via the builder, shareable link or PDF/HTML export.</p>
        </section>

        <section className="mt-8 space-y-3">
          <div className="flex items-center gap-2">
            <Badge className="bg-emerald-600 text-white rounded-md font-pnr">POST</Badge>
            <code data-testid="docs-endpoint" className="font-pnr text-sm text-slate-900">{BASE}/api/convert</code>
          </div>
          <p className="text-sm text-slate-600">Parses a raw PNR/itinerary string and returns enriched flight <code className="font-pnr text-xs bg-slate-100 px-1 rounded">segments</code> plus a rich <code className="font-pnr text-xs bg-slate-100 px-1 rounded">model</code> object containing every non-flight service. Auto-detects Sabre vs Amadeus and reports a confidence score.</p>
        </section>

        <section className="mt-8">
          <h2 className="font-heading font-bold text-slate-900 inline-flex items-center gap-2 mb-3"><KeyRound className="h-4 w-4 text-slate-400" /> Authentication</h2>
          <p className="text-sm text-slate-600 mb-3">Pass your API key as a Bearer token. Requests without a valid key return <code className="font-pnr text-xs bg-slate-100 px-1 rounded">401</code>; the public API requires the Conversion API plan (<code className="font-pnr text-xs bg-slate-100 px-1 rounded">403</code> otherwise). Once your monthly plan limit is reached, requests return <code className="font-pnr text-xs bg-slate-100 px-1 rounded">429</code> until the next billing cycle.</p>
          <CodeBlock label="Header" testid="docs-header" code={`Authorization: Bearer YOUR_API_KEY`} />
        </section>

        <section className="mt-8">
          <h2 className="font-heading font-bold text-slate-900 inline-flex items-center gap-2 mb-3"><Terminal className="h-4 w-4 text-slate-400" /> Example request</h2>
          <CodeBlock label="cURL" testid="docs-request" code={curlExample} />
          <div className="mt-3">
            <CodeBlock label="Request body" testid="docs-body" code={`{\n  "pnr_text": "raw multi-line GDS itinerary string"\n}`} />
          </div>
          <p className="text-xs text-slate-500 mt-2">Send the PNR exactly as displayed in your GDS — including hotel (HHL/9H), car (CCR/9C), cruise, tour, transfer, insurance, seat and e-ticket lines. Multi-line native Sabre and Amadeus formats are supported.</p>
        </section>

        <section className="mt-8">
          <h2 className="font-heading font-bold text-slate-900 inline-flex items-center gap-2 mb-3"><Braces className="h-4 w-4 text-slate-400" /> Example response</h2>
          <CodeBlock label="200 OK — application/json" testid="docs-response" code={responseExample} />
          <p className="text-xs text-slate-500 mt-2">Every array is always present (empty <code className="font-pnr">[]</code> when the PNR has none). In <code className="font-pnr">segments</code>, <code className="font-pnr">date</code> / <code className="font-pnr">arrival_date</code> are short display strings; in <code className="font-pnr">model</code>, dates are ISO <code className="font-pnr">YYYY-MM-DD</code> and times are 24-hour <code className="font-pnr">HH:MM</code>. Anything the parser couldn't confidently map appears in <code className="font-pnr">model.unknownSegments</code>, and any credit-card PAN in the source is masked.</p>
        </section>

        <section className="mt-8">
          <h2 className="font-heading font-bold text-slate-900 inline-flex items-center gap-2 mb-1"><Braces className="h-4 w-4 text-slate-400" /> Response fields</h2>
          <p className="text-xs text-slate-500 mb-3">Complete list of keys returned per segment type. Keys marked optional are omitted when the PNR has no such data; every other key is always present (value may be <code className="font-pnr">null</code>). Airport geo/timezone fields on <code className="font-pnr">segments[]</code> are derived offline from the bundled airports dataset by IATA code.</p>
          <div data-testid="docs-fields" className="space-y-3">
            {FIELDS.map(([title, rows]) => <FieldTable key={title} title={title} rows={rows} />)}
          </div>
        </section>

        <section className="mt-8">
          <h2 className="font-heading font-bold text-slate-900 mb-3">Status codes</h2>
          <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100 text-sm">
            {[
              ["200", "Success — parsed itinerary + model returned", "text-emerald-700"],
              ["401", "Missing or invalid API key", "text-red-700"],
              ["403", "Valid key, but the Conversion API plan is required", "text-orange-700"],
              ["429", "Monthly call limit reached for your plan", "text-amber-700"],
            ].map(([code, desc, color]) => (
              <div key={code} className="flex items-center gap-4 px-4 py-3">
                <span className={`font-pnr font-medium ${color}`}>{code}</span>
                <span className="text-slate-600">{desc}</span>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
